// Type definitions
export type ActionTitleFn = (amount?: number) => string;
export type ActionDescriptionFn =
  | ((name: string) => string)
  | ((name: string, amount: number) => string)
  | ((name: string, amount: number, targetId: string) => string);

export interface ActionConfig {
  title: string | ActionTitleFn;
  description: ActionDescriptionFn;
}

// Utility for consistent amount formatting
const formatAmount = (amount?: number) => amount ?? 0;

export const ACTION_MAPPING_CONFIG: Record<string, ActionConfig> = {
  CUSTOMER_ACCEPT_INVITATION: {
    title: "تم تأكيد انضمام الموكل بالدفع",
    description: (name: string) => `قام ${name} بالانضمام للمعاملة`,
  },
  CUSTOMER_REJECT_INVITATION: {
    title: "تم رفض انضمام الموكل بالدفع",
    description: (name: string) => `قام ${name} برفض الانضمام للمعاملة`,
  },

  // Inspection / Item actions
  FULLY_ACCEPTED: {
    title: "تم قبول جميع البنود",
    description: (name: string) => `قام ${name} بقبول جميع البنود`,
  },

  FULLY_DELIVERED: {
    title: "تم التنفيذ بالكامل",
    description: (name: string) => `قام ${name} بتأكيد التنفيذ بالكامل`,
  },

  // Escalation checks
  CUSTOMER_CHECK_ESCALATION: {
    title: "تم التحقق من التصعيد",
    description: (name: string) => `قام ${name} بالتحقق من حالة التصعيد`,
  },

  PROVIDER_CHECK_ESCALATION: {
    title: "تم التحقق من التصعيد",
    description: (name: string) => `قام ${name} بالتحقق من حالة التصعيد`,
  },

  // Provider reminders
  PROVIDER_REMIND_CUSTOMER_TO_CONFIRM_DELIVERY: {
    title: "تذكير لتأكيد التسليم",
    description: (name: string) => `قام ${name} بتذكير العميل لتأكيد التسليم`,
  },

  PROVIDER_REMIND_CUSTOMER_TO_CONFIRM_INSPECTION: {
    title: "تذكير لتأكيد الفحص",
    description: (name: string) => `قام ${name} بتذكير العميل لتأكيد الفحص`,
  },

  // Admin / Resolution actions
  ADMIN_COMPLETE_RELEASE_FUND_PARTY: {
    title: "اكتمل إصدار الأموال للطرف",
    description: (name: string) => `قام ${name} بإصدار الأموال للطرف بالكامل`,
  },

  ADMIN_CLOSE_REFUND_PARTY: {
    title: "تم إغلاق استرجاع الأموال للطرف",
    description: (name: string) => `قام ${name} بإغلاق استرجاع الأموال للطرف`,
  },

  SYSTEM_CONFIRM_DELIVERY: {
    title: "تم تأكيد التنفيذ تلقائيًا",
    description: () => `تم تأكيد التنفيذ بواسطة النظام`,
  },

  // Admin escalations
  ADMIN_ESCALATE_DELIVERY_FOR_CUSTOMER: {
    title: "تصعيد خلال فترة التنفيذ",
    description: (name: string) =>
      `قام ${name} بتصعيد المعاملة خلال فترة التنفيذ لصالح العميل`,
  },

  ADMIN_ESCALATE_ITEM_FOR_CUSTOMER: {
    title: "تصعيد خلال فترة الفحص",
    description: (name: string) =>
      `قام ${name} بتصعيد المعاملة خلال فترة الفحص لصالح العميل`,
  },

  ADMIN_ESCALATE_DELIVERY_FOR_PROVIDER: {
    title: "تصعيد خلال فترة التنفيذ",
    description: (name: string) =>
      `قام ${name} بتصعيد المعاملة خلال فترة التنفيذ لصالح البائع`,
  },

  ADMIN_ESCALATE_ITEM_FOR_PROVIDER: {
    title: "تصعيد خلال فترة الفحص",
    description: (name: string) =>
      `قام ${name} بتصعيد المعاملة خلال فترة الفحص لصالح البائع`,
  },

  // -------------------------------------
  // Contract creation
  // -------------------------------------
  CUSTOMER_CREATE_CONTRACT: {
    title: "تم إنشاء المعاملة",
    description: (name: string) => `قام ${name} بإنشاء المعاملة.`,
  },
  PROVIDER_CREATE_CONTRACT: {
    title: "تم إنشاء المعاملة",
    description: (name: string) => `قام ${name} بإنشاء المعاملة.`,
  },
  BROKER_CREATE_CONTRACT: {
    title: "تم إنشاء المعاملة",
    description: (name: string) => `قام ${name} بإنشاء المعاملة.`,
  },

  CUSTOMER_REQUEST_PAYMENT_VERIFICATION: {
    title: "مراجعة وتأكيد التحويل",
    description: (actorName: string, amount: number) =>
      `قام ${actorName} بدفع ${amount}﷼ عن طريق تحويل بنكي. يتم الآن مراجعة وتأكيد التحويل.`,
  },

  PARTY_JOINED: {
    title: "قبول طلب الانضمام",
    description: (name: string) =>
      `تم قبول طلب الانضمام للمعاملة من قبل ${name} بنجاح.`,
  },
  RECEIVER_REJECT_CONTRACT: {
    title: "رفض طلب الانضمام",
    description: (name: string) =>
      `تم رفض طلب الانضمام للمعاملة من قبل ${name}.`,
    // NOTE: This mapping assumes RECEIVER_REJECT_CONTRACT covers the rejection of a "joining request."
  },

  ADMIN_VERIFY_PAYMENT: {
    title: "تم التحقق من الدفع",
    description: () => `قام فريق وفّي بالتأكد من صحة التحويل.`,
  },
  ADMIN_DENY_PAYMENT: {
    title: "تم رفض التحويل",
    description: () => `قام فريق وفّي برفض التحويل.`,
  },

  // -------------------------------------
  // Delivery
  // -------------------------------------
  CUSTOMER_DENY_DELIVERY: {
    title: "تصعيد أثناء التوصيل",
    description: (name: string) => `قام ${name} بتصعيد خلال فترة التوصيل.`,
  },
  PROVIDER_ESCALATE_DELIVERY: {
    title: "تصعيد أثناء التوصيل",
    description: (name: string) => `قام ${name} بتصعيد خلال فترة التوصيل.`,
  },
  CUSTOMER_CONFIRM_DELIVERY: {
    title: "تأكيد التوصيل",
    description: (name: string) => `قام ${name} بتأكيد التوصيل.`,
  },
  ADMIN_CONFIRM_DELIVERY: {
    title: "تأكيد التوصيل",
    description: (name: string) => `قام ${name} بتأكيد التوصيل.`,
  },

  // -------------------------------------
  // Item Inspection
  // -------------------------------------
  CUSTOMER_REJECT_ITEM: {
    title: "تصعيد أثناء الفحص",
    description: (name: string) => `قام ${name} بتصعيد خلال فترة الفحص.`,
  },
  PROVIDER_ESCALATE_ITEM: {
    title: "تصعيد أثناء الفحص",
    description: (name: string) => `قام ${name} بتصعيد خلال فترة الفحص.`,
  },
  CUSTOMER_ACCEPT_ITEM: {
    title: "تأكيد الفحص",
    description: (name: string) => `قام ${name} بتأكيد الفحص.`,
  },
  ADMIN_ACCEPT_ITEM: {
    title: "تأكيد الفحص",
    description: (name: string) => `قام ${name} بتأكيد الفحص.`,
  },

  // -------------------------------------
  // Escalation Resolution & Fund Release
  // -------------------------------------
  ADMIN_RESOLVE_ESCALATION_RELEASE_PROVIDER: {
    title: "تم حل المشكلة",
    description: () => `تم حل المشكلة لصالح البائع.`,
  },
  ADMIN_RESOLVE_ESCALATION_REFUND_CUSTOMER: {
    title: "تم حل المشكلة",
    description: () => `تم حل المشكلة لصالح المشتري.`,
  },
  ADMIN_CLOSE_RELEASE_FUND_TO_PROVIDER: {
    title: "تحويل الأموال",
    description: () => `تم تحويل الأموال إلى البائع`,
  },
  ADMIN_COMPLETE_RELEASE_FUND_TO_PROVIDER: {
    title: "تحويل الأموال",
    description: () => `تم تحويل الأموال إلى حساب البائع.`,
  },
  ADMIN_CLOSE_REFUND_CUSTOMER: {
    title: "تم إغلاق التصعيد",
    description: () => `تم إغلاق التصعيد لصالح المشتري.`,
  },

  // -------------------------------------
  // KEEP YOUR OTHER ACTIONS AS-IS BELOW
  // -------------------------------------
  // Contract actions
  SENDER_CREATE_CONTRACT: {
    title: "تم إنشاء العقد",
    description: (name: string) => `قام ${name} بإنشاء عقد جديد`,
  },
  SENDER_ARCHIVE_CONTRACT: {
    title: "تم أرشفة العقد",
    description: (name: string) => `قام ${name} بأرشفة العقد`,
  },
  SENDER_DELETE_CONTRACT: {
    title: "تم حذف العقد",
    description: (name: string) => `قام ${name} بحذف العقد`,
  },
  ADMIN_DELETE_CONTRACT: {
    title: "تم حذف العقد من قبل المسؤول",
    description: (name: string) => `قام المسؤول ${name} بحذف العقد`,
  },
  RECEIVER_ACCEPT_CONTRACT: {
    title: "تم قبول العقد",
    description: (name: string) => `قام ${name} بقبول العقد`,
  },

  SELLER_APPROVE_CONTRACT: {
    title: "تم الموافقة على العقد",
    description: (name: string) => `قام ${name} بالموافقة على العقد`,
  },
  SELLER_REJECT_CONTRACT: {
    title: "تم رفض العقد من البائع",
    description: (name: string) => `قام ${name} برفض العقد`,
  },
  SYSTEM_REJECT_CONTRACT: {
    title: "تم رفض العقد تلقائيًا",
    description: (name: string) => `تم رفض العقد بواسطة النظام`,
  },
  DELETE_NOT_PAID_CONTRACT: {
    title: "تم حذف العقد غير المدفوع",
    description: (name: string) => `تم حذف العقد لعدم وجود دفعات`,
  },
  BACK_TO_SUBMITTED: {
    title: "تم إعادة العقد إلى الحالة المقدمة",
    description: (name: string) =>
      `تمت إعادة العقد بواسطة ${name} إلى الحالة المقدمة`,
  },
  BACK_TO_IN_PROGRESS: {
    title: "تم إعادة العقد إلى قيد التنفيذ",
    description: (name: string) =>
      `تمت إعادة العقد بواسطة ${name} إلى قيد التنفيذ`,
  },
  PAUSE_CONTRACT: {
    title: "تم إيقاف العقد مؤقتًا",
    description: (name: string) => `قام ${name} بإيقاف العقد مؤقتًا`,
  },
  RESUME_CONTRACT: {
    title: "تم استئناف العقد",
    description: (name: string) => `قام ${name} باستئناف العقد`,
  },
  // Payment & fund actions
  CUSTOMER_FUND: {
    // Maps to: تم دفع 200 درهم بنجاح
    title: (amount?: number) => `تم دفع ${formatAmount(amount)} بنجاح`,
    // Maps to: تم دفع 200 درهم بنجاح من عمار عدنان
    description: (actorName: string, amount: number) =>
      amount
        ? `تم دفع ${amount}﷼ بنجاح من ${actorName}`
        : `قام ${actorName} بدفع القيمة الموكلة إليه`,
  },

  CUSTOMER_UPLOAD_PAYMENT_DOCS: {
    title: "تم تحميل مستند الدفع",
    description: (name: string) => `قام ${name} بتحميل مستند الدفع`,
  },

  CASH_OUT: {
    title: (amount?: number) => `تم تحويل ${formatAmount(amount)} ريال`,
    description: (name: string, amount: number, targetId: string) =>
      `قام ${name} بتحويل ${amount}﷼ إلى ${targetId}`,
  },
  CASH_OUT_CONFIRMED: {
    title: "تم تأكيد التحويل",
    description: (name: string, amount: number, targetId: string) =>
      `قام ${name} بتحويل ${amount}﷼ إلى ${targetId}`,
  },
  MONEY_BACKED_TO_USER_CONFIRMED: {
    title: "تم تأكيد الاسترداد",
    description: (name: string, amount: number, targetId: string) =>
      `قام ${name} بإرجاع ${amount}﷼ إلى ${targetId}`,
  },
  CASH_OUT_FAILED: {
    title: "فشل تحويل الأموال",
    description: (name: string) => `فشل تحويل الأموال بواسطة ${name}`,
  },
  FULLY_FUNDED: {
    title: "تم تمويل العقد بالكامل",
    description: (name: string) => `تم تمويل العقد بالكامل بواسطة ${name}`,
  },
  FULLY_REFUNDED: {
    title: "تم استرجاع الأموال بالكامل",
    description: (name: string) => `تم استرجاع الأموال بالكامل بواسطة ${name}`,
  },
  FULLY_RELEASED: {
    title: "تم إصدار الأموال بالكامل",
    description: (name: string) => `تم إصدار الأموال بالكامل بواسطة ${name}`,
  },
  // Delivery actions
  SENDER_REQUIRED_SHIPMENT: {
    title: "تم طلب الشحن",
    description: (name: string) => `قام ${name} بطلب الشحن`,
  },
  SENDER_ADD_SHIPMENT_DETAILS: {
    title: "تم إضافة تفاصيل الشحن",
    description: (name: string) => `قام ${name} بإضافة تفاصيل الشحن`,
  },
  PROVIDER_SEND_DELIVERY: {
    title: "تم إرسال الشحنة",
    description: (name: string) => `قام ${name} بإرسال الشحنة`,
  },

  CONTRACT_SHIPPED: {
    title: "تم شحن العقد",
    description: (name: string) => `تم شحن العقد بواسطة ${name}`,
  },
  CONTRACT_DELIVERED: {
    title: "تم تسليم العقد",
    description: (name: string) => `تم تسليم العقد بواسطة ${name}`,
  },
  AFTER_CONTRACT_DELIVERY: {
    title: "بعد التسليم",
    description: (name: string) =>
      `تم تحديث حالة العقد بعد التسليم بواسطة ${name}`,
  },

  // System / Automatic actions
  SYSTEM_ACCEPT_ITEM: {
    title: "تم تأكيد الفحص تلقائيًا",
    description: (name: string) => `تم قبول العنصر بواسطة النظام`,
  },
  SYSTEM_TRIGGER_COMPLEX_CONTRACT: {
    title: "تم تفعيل العقد المعقد تلقائيًا",
    description: (name: string) => `تم تفعيل العقد المعقد بواسطة النظام`,
  },
  SYSTEM_UPDATE_STOCK: {
    title: "تم تحديث المخزون تلقائيًا",
    description: (name: string) => `تم تحديث المخزون بواسطة النظام`,
  },
  SYSTEM_SETTLE_ITEM: {
    title: "تم التسوية تلقائيًا",
    description: (name: string) => `تم تسوية العنصر بواسطة النظام`,
  },

  // Broker / Customer actions
  BROKER_ACCEPT_ITEM: {
    title: "تم تأكيد الفحص من قبل الوسيط",
    description: (name: string) => `قام الوسيط ${name} بقبول العنصر`,
  },
  BROKER_REFUND_CUSTOMER: {
    title: "تم رد الأموال للعميل بواسطة الوسيط",
    description: (name: string) => `قام الوسيط ${name} برد الأموال للعميل`,
  },
  BROKER_ESCALATE_DELIVERY: {
    title: "تصعيد التسليم بواسطة الوسيط",
    description: (name: string) => `قام الوسيط ${name} بتصعيد التسليم`,
  },

  // Customer / Provider actions
  CUSTOMER_INITIATE_CONTRACT: {
    title: "تم بدء العقد بواسطة العميل",
    description: (name: string) => `قام العميل ${name} ببدء العقد`,
  },
  PROVIDER_INITIATE_CONTRACT: {
    title: "تم بدء العقد بواسطة البائع",
    description: (name: string) => `قام البائع ${name} ببدء العقد`,
  },

  PROVIDER_DELETE_CONTRACT: {
    title: "تم حذف العقد بواسطة البائع",
    description: (name: string) => `قام البائع ${name} بحذف العقد`,
  },
  CUSTOMER_DELETE_CONTRACT: {
    title: "تم حذف العقد بواسطة العميل",
    description: (name: string) => `قام العميل ${name} بحذف العقد`,
  },
  CLIENT_DELETE_CONTRACT: {
    title: "تم حذف العقد بواسطة الطرف",
    description: (name: string) => `قام الطرف ${name} بحذف العقد`,
  },

  // Invoice / Settlement / Milestones
  INVOICE_CREATED: {
    title: "تم إنشاء الفاتورة",
    description: (name: string) => `تم إنشاء الفاتورة بواسطة ${name}`,
  },
  INVOICE_URL_GENERATED: {
    title: "تم إنشاء رابط الفاتورة",
    description: (name: string) => `تم إنشاء رابط الفاتورة بواسطة ${name}`,
  },
  ADMIN_SETTLE_CONTRACT: {
    title: "تم تسوية العقد من قبل المسؤول",
    description: (name: string) => `قام ${name} بتسوية العقد`,
  },
  ADMIN_RESETTLE_CONTRACT: {
    title: "تم إعادة تسوية العقد من قبل المسؤول",
    description: (name: string) => `قام ${name} بإعادة تسوية العقد`,
  },
  ADMIN_DRAFT_SETTLEMENT_CONTRACT: {
    title: "تم إنشاء مسودة التسوية",
    description: (name: string) => `قام ${name} بإنشاء مسودة التسوية`,
  },
  MILESTONE_CONTRACT_ADD_PARTIES: {
    title: "تم إضافة أطراف جديدة للعقد",
    description: (name: string) => `قام ${name} بإضافة أطراف جديدة للعقد`,
  },
  CLIENT_ADMIN_ADD_PARTY_TO_CONTRACT: {
    title: "تم إضافة طرف للعقد بواسطة المسؤول",
    description: (name: string) => `قام المسؤول ${name} بإضافة طرف للعقد`,
  },

  INVITE_PARTIES: {
    title: "تم دعوة الأطراف",
    description: (name: string) => `قام ${name} بدعوة الأطراف للانضمام للعقد`,
  },
  REINVITE_PARTIES: {
    title: "تم إعادة دعوة الأطراف",
    description: (name: string) => `قام ${name} بإعادة دعوة الأطراف للعقد`,
  },
  PARTY_CONFIRMED: {
    title: "تم تأكيد انضمام الطرف",
    description: (name: string) => `قام ${name} بتأكيد الانضمام للعقد`,
  },

  // Stock / Updates
  SENDER_UPDATE_STOCK: {
    title: "تم تحديث المخزون بواسطة المرسل",
    description: (name: string) => `قام ${name} بتحديث المخزون`,
  },

  // System triggers & complex contracts
  TRIGGER_CHOICE: {
    title: "تم تفعيل خيار",
    description: (name: string) => `تم تفعيل خيار بواسطة ${name}`,
  },
  COMPLETE_COMPLEX_CONTRACT: {
    title: "تم إكمال العقد المعقد",
    description: (name: string) => `تم إكمال العقد المعقد بواسطة ${name}`,
  },

  // Admin / Arbitrator actions
  ARBITRATOR_CONFIRM_DELIVERY: {
    title: "تم تأكيد التسليم بواسطة المحكم",
    description: (name: string) => `قام المحكم ${name} بتأكيد التسليم`,
  },
  ARBITRATOR_ACCEPT_ITEM: {
    title: "تم تأكيد الفحص بواسطة المحكم",
    description: (name: string) => `قام المحكم ${name} بتأكيد الفحص`,
  },
  ARBITRATOR_RESOLVE_REFUND_CUSTOMER: {
    title: "تم حل نزاع استرجاع الأموال للعميل بواسطة المحكم",
    description: (name: string) =>
      `قام المحكم ${name} بحل نزاع استرجاع الأموال للعميل`,
  },

  // Admin / Misc
  ADMIN_RESET_METRIC: {
    title: "تم إعادة تعيين المقاييس",
    description: (name: string) => `قام ${name} بإعادة تعيين المقاييس`,
  },
  ADMIN_RE_OPEN_CONTRACT: {
    title: "تم إعادة فتح العقد",
    description: (name: string) => `قام ${name} بإعادة فتح العقد`,
  },
  ALLOW_TABBY: {
    title: "تم تفعيل خدمة تابّي",
    description: (name: string) => `قام ${name} بتفعيل خدمة تابّي`,
  },
  ALLOW_NEO: {
    title: "تم تفعيل خدمة نيو",
    description: (name: string) => `قام ${name} بتفعيل خدمة نيو`,
  },
  ALLOW_BUY_NOW_PAY_LATER: {
    title: "تم تفعيل خدمة اشتر الآن وادفع لاحقاً",
    description: (name: string) =>
      `قام ${name} بتفعيل خدمة اشتر الآن وادفع لاحقاً`,
  },
  CUSTOMER_CHANGE_REQUESTED: {
    title: "تم طلب التغيير من العميل",
    description: (name: string) => `قام ${name} بطلب تغيير العقد`,
  },
  ADD_SHIPPING_INFO: {
    title: "تم إضافة معلومات الشحن",
    description: (name: string) => `قام ${name} بإضافة معلومات الشحن`,
  },
};
